/**
 * Reducers for the /admin/app-config definition sync.
 *
 * Three rules worth knowing.
 *
 * A failed scan does NOT clear `liveDefinitions` — a stale report the admin
 * can see beats a blank screen, and `scanError` says it is stale.
 *
 * Every mutation outcome is recorded against its own `configKey`, so one
 * failing row never blanks another row's result.
 *
 * And success is recorded too, not just failure. The page re-scans after every
 * write, which moves a registered key out of Missing and into In sync — so
 * without a marker the only visible outcome of a successful click is the row
 * disappearing from where the operator was looking. `successByKey` is
 * transient, expired on a timer by the saga, and is dropped the moment the
 * same key fails: a retry that fails must not leave the previous attempt's
 * tick beside the new error.
 */
import { createReducer } from '@reduxjs/toolkit';
import {
  ADD_DEFINITIONS_FAILURE,
  ADD_DEFINITIONS_REQUEST,
  ADD_DEFINITIONS_SUCCESS,
  CHECK_ROOT_ORGANIZATION_RESULT,
  CLEAR_DEFINITION_ERROR,
  CLEAR_DEFINITION_SUCCESS,
  DELETE_DEFINITION_FAILURE,
  DELETE_DEFINITION_REQUEST,
  DELETE_DEFINITION_SUCCESS,
  SCAN_DEFINITIONS_FAILURE,
  SET_DEFAULT_REQUEST,
  SCAN_DEFINITIONS_REQUEST,
  SCAN_DEFINITIONS_SUCCESS,
  UPDATE_DEFINITION_FAILURE,
  UPDATE_DEFINITION_REQUEST,
  UPDATE_DEFINITION_SUCCESS,
} from '../actions';
import { AdminState, DefinitionSuccess, KeyedFailure } from '../models';
import { defaultState, namespace } from '../store';

const withPending = (
  state: AdminState,
  configKeys: readonly string[]
): AdminState => {
  const pending = new Set(state.pendingKeys);
  const errors = { ...state.errorsByKey };
  for (const key of configKeys) {
    pending.add(key);
    // A retry starts clean; the previous error is no longer the outcome.
    delete errors[key];
  }
  return { ...state, pendingKeys: [...pending], errorsByKey: errors };
};

const withoutPending = (
  state: AdminState,
  configKeys: readonly string[]
): AdminState => ({
  ...state,
  pendingKeys: state.pendingKeys.filter((key) => !configKeys.includes(key)),
});

/**
 * Records what just worked, per key, so the row can say so before the re-scan
 * moves it to another bucket.
 */
const withSuccess = (
  state: AdminState,
  configKeys: readonly string[],
  outcome: DefinitionSuccess
): AdminState => {
  const success = { ...state.successByKey };
  for (const key of configKeys) {
    success[key] = outcome;
  }
  return { ...withoutPending(state, configKeys), successByKey: success };
};

const withFailure = (
  state: AdminState,
  { configKeys, message }: KeyedFailure
): AdminState => {
  const errors = { ...state.errorsByKey };
  const success = { ...state.successByKey };
  for (const key of configKeys) {
    errors[key] = message;
    // A retry that fails must not leave the previous attempt's tick behind it.
    delete success[key];
  }
  return {
    ...withoutPending(state, configKeys),
    errorsByKey: errors,
    successByKey: success,
  };
};

export const adminReducer = {
  [namespace]: createReducer(defaultState, (builder) => {
    builder
      .addCase(SCAN_DEFINITIONS_REQUEST, (state) => ({
        ...state,
        isScanning: true,
        scanError: undefined,
      }))
      .addCase(SCAN_DEFINITIONS_SUCCESS, (state, action) => ({
        ...state,
        isScanning: false,
        hasScanned: true,
        scanError: undefined,
        liveDefinitions: action.payload.records,
        valuesUnavailable: action.payload.valuesUnavailable,
        scannedAt: action.payload.scannedAt,
      }))
      .addCase(SCAN_DEFINITIONS_FAILURE, (state, action) => ({
        ...state,
        isScanning: false,
        scanError: action.payload.message,
      }))
      .addCase(CHECK_ROOT_ORGANIZATION_RESULT, (state, action) => ({
        ...state,
        rootOrgStatus: action.payload.status,
        organizationName: action.payload.organizationName,
      }))
      .addCase(ADD_DEFINITIONS_REQUEST, (state, action) =>
        withPending(
          state,
          action.payload.definitions.map((d) => d.configKey)
        )
      )
      .addCase(ADD_DEFINITIONS_SUCCESS, (state, action) =>
        withSuccess(state, action.payload.configKeys, 'added')
      )
      .addCase(ADD_DEFINITIONS_FAILURE, (state, action) =>
        withFailure(state, action.payload)
      )
      .addCase(UPDATE_DEFINITION_REQUEST, (state, action) =>
        withPending(state, [action.payload.definition.configKey])
      )
      .addCase(SET_DEFAULT_REQUEST, (state, action) =>
        withPending(state, [action.payload.configKey])
      )
      .addCase(UPDATE_DEFINITION_SUCCESS, (state, action) =>
        withSuccess(state, [action.payload.configKey], 'updated')
      )
      .addCase(UPDATE_DEFINITION_FAILURE, (state, action) =>
        withFailure(state, action.payload)
      )
      .addCase(DELETE_DEFINITION_REQUEST, (state, action) =>
        withPending(state, [action.payload.configKey])
      )
      .addCase(DELETE_DEFINITION_SUCCESS, (state, action) =>
        withSuccess(state, [action.payload.configKey], 'deleted')
      )
      .addCase(DELETE_DEFINITION_FAILURE, (state, action) =>
        withFailure(state, action.payload)
      )
      .addCase(CLEAR_DEFINITION_SUCCESS, (state, action) => {
        const success = { ...state.successByKey };
        for (const key of action.payload.configKeys) {
          delete success[key];
        }
        return { ...state, successByKey: success };
      })
      .addCase(CLEAR_DEFINITION_ERROR, (state, action) => {
        const errors = { ...state.errorsByKey };
        delete errors[action.payload.configKey];
        return { ...state, errorsByKey: errors };
      });
  }),
};
